// Watches the drawn trains for what a viewer would call a glitch, and keeps
// the last few minutes of them for "Report a glitch": a train that moved
// JUMP_PX or more in one frame, or one that went from drawn to gone without
// fading, while the camera stood still. TfL and National Rail alike.

// px in one frame: far more than any train moves between frames.
const JUMP_PX = 40;
// ms of glitches kept, and at most how many.
const KEEP_MS = 5 * 60_000;
const KEEP_MAX = 200;

// px from the edge: a train there may just be leaving the view.
const EDGE_PX = 8;

// A train as drawn this frame, in container pixels.
export interface Drawn {
  readonly id: string;
  readonly x: number;
  readonly y: number;
  readonly alpha: number;
  // What it is, for the report: line, headcode or destination, berth.
  readonly label: string;
}

export interface Glitch {
  readonly at: number;
  // jump: moved JUMP_PX or more; vanish: gone; snap: still there but
  // suddenly faint (TfL's crossfade to a new position).
  readonly kind: 'jump' | 'vanish' | 'snap';
  readonly id: string;
  readonly label: string;
  readonly from: readonly [number, number];
  readonly to: readonly [number, number] | null;
}

export interface GlitchWatcher {
  // After each animation frame. camera: anything that changes when the view
  // does (then positions aren't compared); width, height: the view's size.
  readonly frame: (now: number, camera: string, drawn: readonly Drawn[], width: number, height: number) => void;
  readonly recent: (now: number) => readonly Glitch[];
}

export function glitchWatcher(): GlitchWatcher {
  let last = new Map<string, Drawn>();
  let lastCamera = '';
  const glitches: Glitch[] = [];
  const keep = (g: Glitch): void => {
    glitches.push(g);
    if (glitches.length > KEEP_MAX) glitches.shift();
  };
  return {
    frame(now, camera, drawn, width, height) {
      const inside = (d: Drawn): boolean =>
        d.x > EDGE_PX && d.y > EDGE_PX && d.x < width - EDGE_PX && d.y < height - EDGE_PX;
      const next = new Map<string, Drawn>();
      for (const d of drawn) next.set(d.id, d);
      if (camera === lastCamera) {
        for (const [id, was] of last) {
          if (was.alpha < 0.5 || !inside(was)) continue;
          const is = next.get(id);
          if (!is) {
            keep({ at: now, kind: 'vanish', id, label: was.label, from: [was.x, was.y], to: null });
          } else if (is.alpha < 0.1) {
            keep({ at: now, kind: 'snap', id, label: is.label, from: [was.x, was.y], to: [is.x, is.y] });
          } else if (is.alpha >= 0.5 && Math.hypot(is.x - was.x, is.y - was.y) >= JUMP_PX) {
            keep({ at: now, kind: 'jump', id, label: is.label, from: [was.x, was.y], to: [is.x, is.y] });
          }
        }
      }
      last = next;
      lastCamera = camera;
    },
    recent: (now) => glitches.filter((g) => now - g.at <= KEEP_MS),
  };
}
